import React, { useCallback, useMemo, useState } from 'react';

import {
  Grid,
  Text,
  NewTextField as TextField,
  useMediaQuery,
  useTheme,
} from '@lp/ds-next';

import OfferConfigurationForm, {
  ERROR_PARSE_JSON_MESSAGE,
} from './OfferConfigurationFrom';
import { IServiceLevel } from '../models/serviceLevel';
import { SLCase, SLCaseType } from '../types';
import CancelSubmitFooterModal from '@/components/CancelSubmitFooterModal';
import CustomModal from '@/components/CustomModal';
import useError from '@/hooks/useError';

interface ServiceLevelFormProps {
  initialServiceLevel: any;
  onSubmit: (body: IServiceLevel, id?: string) => Promise<void>;
  onSubmitDone?: () => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  fetchServiceLevels: () => void;
  isSameConfigInvalid?: boolean;
  title?: string;
  subtitle?: string;
  cloneMode?: boolean;
  slCaseType?: SLCaseType;
}

interface StateServiceLevel {
  name: string;
  description: string;
  productConfiguration: string;
  offerConfiguration: any;
}

const ServiceLevelForm = ({
  initialServiceLevel,
  onSubmit,
  onSubmitDone = () => {},
  loading,
  setLoading,
  visible,
  setVisible,
  fetchServiceLevels,
  isSameConfigInvalid = true,
  title = 'Add a Service Level',
  subtitle,
  cloneMode = false,
  slCaseType = SLCase.CAMEL,
}: ServiceLevelFormProps) => {
  const addError = useError();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  const removeOfferFields = useCallback((offerConfiguration, slCaseType) => {
    if (slCaseType === SLCase.SNAKE) {
      const {
        product_configuration,
        name,
        description,
        humidity_sensor,
        humidity_threshold,
        pressure_sensor,
        pressure_threshold,
        temperature_sensor,
        temperature_threshold,
        shock_sensor,
        shock_threshold,
        ...cleanOffer
      } = offerConfiguration;

      return cleanOffer;
    }

    const {
      productConfiguration,
      name,
      description,
      humiditySensor,
      humidityThreshold,
      pressureSensor,
      pressureThreshold,
      temperatureSensor,
      temperatureThreshold,
      shockSensor,
      shockThreshold,
      ...cleanOffer
    } = offerConfiguration;

    return cleanOffer;
  }, []);

  const formatInitialServiceLevel = useMemo(() => {
    switch (slCaseType) {
      case SLCase.CAMEL:
        return {
          name: initialServiceLevel.name,
          description: initialServiceLevel.description,
          productConfiguration: JSON.stringify(
            initialServiceLevel.productConfiguration,
            null,
            2
          ),
          offerConfiguration: {
            otherValues: JSON.stringify(
              removeOfferFields(initialServiceLevel, slCaseType),
              null,
              2
            ),
            humiditySensor: {
              enabled: initialServiceLevel.humiditySensor,
              values: JSON.stringify(
                initialServiceLevel.humidityThreshold,
                null,
                2
              ),
            },
            pressureSensor: {
              enabled: initialServiceLevel.pressureSensor,
            },
            temperatureSensor: {
              enabled: initialServiceLevel.temperatureSensor,
              values: JSON.stringify(
                initialServiceLevel.temperatureThreshold,
                null,
                2
              ),
            },
            shockSensor: {
              enabled: initialServiceLevel.shockSensor,
              values: JSON.stringify(
                initialServiceLevel.shockThreshold,
                null,
                2
              ),
            },
          },
        };
      case SLCase.SNAKE:
        return {
          name: initialServiceLevel.name,
          description: initialServiceLevel.description,
          productConfiguration: JSON.stringify(
            initialServiceLevel.product_configuration,
            null,
            2
          ),
          offerConfiguration: {
            otherValues: JSON.stringify(
              removeOfferFields(initialServiceLevel, slCaseType),
              null,
              2
            ),
            humiditySensor: {
              enabled: initialServiceLevel.humidity_sensor,
              values: JSON.stringify(
                initialServiceLevel.humidity_threshold,
                null,
                2
              ),
            },
            pressureSensor: {
              enabled: initialServiceLevel.pressure_sensor,
            },
            temperatureSensor: {
              enabled: initialServiceLevel.temperature_sensor,
              values: JSON.stringify(
                initialServiceLevel.temperature_threshold,
                null,
                2
              ),
            },
            shockSensor: {
              enabled: initialServiceLevel.shock_sensor,
              values: JSON.stringify(
                initialServiceLevel.shock_threshold,
                null,
                2
              ),
            },
          },
        };
    }
  }, [initialServiceLevel, removeOfferFields, slCaseType]);

  const [serviceLevel, setServiceLevel] = useState<StateServiceLevel>(
    formatInitialServiceLevel
  );

  const isInvalid = () => {
    let returnObj: Record<string, boolean> = {};

    if (serviceLevel.productConfiguration) {
      try {
        JSON.parse(serviceLevel.productConfiguration);
      } catch (e) {
        returnObj = {
          ...returnObj,
          productConfiguration: true,
          isInvalid: true,
        };
      }
    }

    if (serviceLevel.offerConfiguration.otherValues) {
      try {
        JSON.parse(serviceLevel.offerConfiguration.otherValues);
      } catch (error) {
        returnObj = { ...returnObj, otherValues: true, isInvalid: true };
      }
    }

    if (serviceLevel.offerConfiguration.humiditySensor) {
      try {
        JSON.parse(serviceLevel.offerConfiguration.humiditySensor.values);
      } catch (error) {
        returnObj = { ...returnObj, humiditySensor: true, isInvalid: true };
      }
    }

    if (serviceLevel.offerConfiguration.temperatureSensor) {
      try {
        JSON.parse(serviceLevel.offerConfiguration.temperatureSensor.values);
      } catch (error) {
        returnObj = { ...returnObj, temperatureSensor: true, isInvalid: true };
      }
    }

    if (serviceLevel.offerConfiguration.shockSensor) {
      try {
        JSON.parse(serviceLevel.offerConfiguration.shockSensor.values);
      } catch (error) {
        returnObj = { ...returnObj, shockSensor: true, isInvalid: true };
      }
    }

    if (cloneMode) {
      returnObj = {
        ...returnObj,
        isInvalid:
          returnObj.isInvalid ||
          !serviceLevel.name ||
          !serviceLevel.description ||
          serviceLevel.name === formatInitialServiceLevel.name ||
          serviceLevel.description === formatInitialServiceLevel.description,
      };

      return returnObj;
    }
    if (isSameConfigInvalid) {
      returnObj = {
        ...returnObj,
        isInvalid:
          returnObj.isInvalid ||
          !serviceLevel.name ||
          !serviceLevel.description ||
          (serviceLevel.productConfiguration ===
            formatInitialServiceLevel.productConfiguration &&
            serviceLevel.offerConfiguration ===
              formatInitialServiceLevel.productConfiguration),
      };
    }

    returnObj = {
      ...returnObj,
      isInvalid:
        returnObj.isInvalid ||
        (serviceLevel.name === formatInitialServiceLevel.name &&
          serviceLevel.description === formatInitialServiceLevel.description &&
          serviceLevel.productConfiguration ===
            formatInitialServiceLevel.productConfiguration &&
          serviceLevel.offerConfiguration ===
            formatInitialServiceLevel.productConfiguration),
    };

    return returnObj;
  };

  const formatSensorsData = useCallback(
    () => ({
      humiditySensor: serviceLevel.offerConfiguration.humiditySensor.enabled,
      humidityThreshold: {
        percentMin: JSON.parse(
          serviceLevel.offerConfiguration.humiditySensor.values
        ).percentMin,
        percentMax: JSON.parse(
          serviceLevel.offerConfiguration.humiditySensor.values
        ).percentMax,
      },

      pressureSensor: serviceLevel.offerConfiguration.pressureSensor.enabled,
      temperatureSensor:
        serviceLevel.offerConfiguration.temperatureSensor.enabled,
      temperatureThreshold: {
        celsiusMin: JSON.parse(
          serviceLevel.offerConfiguration.temperatureSensor.values
        ).celsiusMin,
        celsiusMax: JSON.parse(
          serviceLevel.offerConfiguration.temperatureSensor.values
        ).celsiusMax,
      },
      shockSensor: serviceLevel.offerConfiguration.shockSensor.enabled,
      shockThreshold: JSON.parse(
        serviceLevel.offerConfiguration.shockSensor.values
      ),
    }),
    [serviceLevel.offerConfiguration]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const otherValues = JSON.parse(
        serviceLevel.offerConfiguration.otherValues
      );
      const sensorsData = formatSensorsData();
      const { offerConfiguration, ...cleanService } = serviceLevel;

      await onSubmit({
        ...cleanService,
        ...otherValues,
        ...sensorsData,
        productConfiguration: JSON.parse(serviceLevel.productConfiguration),
      });
      fetchServiceLevels();
      setVisible(false);

      onSubmitDone();
    } catch (e) {
      let errorValue = '';

      if (e instanceof Object) {
        errorValue = ` : "${
          e['response'].data.message.split(/"(.*?)"/g)[1]
        }" value is wrong because [format], [type] or [value} is not correct`;
      }

      addError(`There was an error ${errorValue}`);
      setLoading(false);

      return;
    }
  }, [
    addError,
    onSubmit,
    onSubmitDone,
    setVisible,
    fetchServiceLevels,
    serviceLevel,
    formatSensorsData,
    setLoading,
  ]);

  const handleChange = useCallback((event) => {
    setServiceLevel((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  }, []);

  const modalFooterButtons = CancelSubmitFooterModal(
    isInvalid().isInvalid,
    handleSubmit,
    () => setVisible(false),
    loading
  );

  return (
    <CustomModal
      destroyOnClose
      title={
        <>
          {title}
          <Text variant="bodyTextS" color="custom.neutral.black.50">
            {subtitle}
          </Text>
        </>
      }
      open={visible}
      footer={modalFooterButtons}
      onCancel={() => setVisible(false)}
      {...(!isMobile ? { width: '80%' } : {})}
      style={{ maxWidth: '1280px' }}
      centered={!isMobile}
    >
      <Grid container spacing={1}>
        <Grid item mobile={12} tablet={6} laptop={4}>
          <Grid container direction="column" spacing={1}>
            <Grid item mobile={12}>
              <TextField
                onChange={handleChange}
                placeholder="Name"
                label="Name"
                name="name"
                value={serviceLevel.name}
                fullWidth
              />
            </Grid>
            <Grid item mobile={12}>
              <TextField
                onChange={handleChange}
                placeholder="Description"
                label="Description"
                name="description"
                value={serviceLevel.description}
                fullWidth
              />
            </Grid>
            <Grid item mobile={12}>
              <TextField
                multiline
                fullWidth
                onChange={handleChange}
                placeholder="Box Configuration"
                label="Box Configuration"
                name="productConfiguration"
                value={serviceLevel.productConfiguration}
                error={isInvalid().productConfiguration}
                helperText={ERROR_PARSE_JSON_MESSAGE}
                maxRows={21.5}
              />
            </Grid>
          </Grid>
        </Grid>
        <OfferConfigurationForm
          initialOffer={serviceLevel.offerConfiguration}
          setServiceLevel={setServiceLevel}
          errors={isInvalid()}
        />
      </Grid>
    </CustomModal>
  );
};

export default ServiceLevelForm;
